/**
 * Editable backlog with readiness feedback, personal browser inference, and batch deletion. Keeps
 * inline drafts and save versions, debounces writes, and merges delayed responses with current
 * edits. Refresh uses task snapshots; inference fills eligible missing values and leaves final
 * scheduling to the server. Imported duplicate detection affects visible backlog presentation.
 * Rows have a shallow memo boundary and stable action callbacks: toggling selection updates only
 * changed rows instead of rebuilding every input and bundle picker. See docs/developer/ui-latency.md.
 *
 * @module apps/web/src/pages/UnscheduledPage
 */

import {
  memo,
  useCallback,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Pencil,
  RefreshCw,
  Trash2,
  WandSparkles,
} from 'lucide-react';
import type { AttributeDef, TaskStateProfile, TaskUI, WorkspaceBundleUI } from '@fluxability/types';
import { deriveTaskStateMode, numberToPriority, taskToUI } from '@fluxability/types';
import { api } from '@/lib/api';
import { afterNextPaint } from '@/lib/afterPaint';
import { inferActivityPlanning } from '@/lib/browserInference';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TaskEditor } from '@/components/blocks/TaskEditor';
import { Detached, useDetachedState } from '@/hooks/useDetachedState';
import { EnumAttributeInput } from '@/components/attributes/EnumAttributeInput';
import { useToast } from '@/hooks/use-toast';
import { useRouteTasks } from '@/stores/routeData';
import { useTasksStore, captureTaskSnapshot } from '@/stores/tasksStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { useBundlePurposeGate } from '@/components/workspace/BundlePurposeDialog';
import {
  formatMissingPlanningFields,
  getMissingPlanningFields,
  getPlanningAttributeValue,
} from '@/lib/activityReadiness';
import { cn } from '@/lib/utils';

/**
 * Editable backlog with readiness feedback, personal browser inference, and batch deletion. Keeps
 * inline drafts and save versions, debounces writes, and merges delayed responses with current
 * edits. Refresh uses task snapshots; inference fills eligible missing values and leaves final
 * scheduling to the server. Imported duplicate detection affects visible backlog presentation.
 */
export function UnscheduledPage() {
  const { toast } = useToast();
  const tasks = useRouteTasks();
  const setTasks = useTasksStore(s => s.setTasks);
  const updateTask = useTasksStore(s => s.updateTask);
  const deleteTasks = useTasksStore(s => s.deleteTasks);
  const bundles = useWorkspaceStore(s => s.bundles);
  const loadWorkspace = useWorkspaceStore(s => s.loadWorkspace);
  // The editor owns the open activity, so opening it re-renders the editor, not every row.
  const editor = useDetachedState<TaskUI | null>();
  const setEditingTask = editor.set;
  const purposeGate = useBundlePurposeGate();
  const [refreshing, setRefreshing] = useState(false);
  const [inferring, setInferring] = useState(false);
  const [inferenceStatus, setInferenceStatus] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const deletedIdsRef = useRef(new Set<string>());
  const refreshVersionRef = useRef(0);
  const [availableAttributes, setAvailableAttributes] = useState<AttributeDef[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Partial<TaskUI>>>({});
  const [autoSaveStatus, setAutoSaveStatus] = useState<
    Record<string, 'saving' | 'saved' | 'error'>
  >({});
  const [visibleLimit, setVisibleLimit] = useState(80);
  const draftsRef = useRef<Record<string, Partial<TaskUI>>>({});
  const autoSaveTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const draftVersionsRef = useRef<Record<string, number>>({});
  const availableAttributesRef = useRef<AttributeDef[]>([]);

  /** Refresh against a captured store revision; preserve newer drafts and ignore superseded requests. */
  const refresh = useCallback(async () => {
    const version = ++refreshVersionRef.current;
    setRefreshing(true);
    try {
      const snapshot = captureTaskSnapshot();
      const response = await api.tasks.list();
      if (version === refreshVersionRef.current && response.success && response.data) {
        const tasksList = Array.isArray(response.data) ? response.data : response.data?.tasks;
        if (tasksList) {
          const nextTasks = tasksList
            .map(taskToUI)
            .filter((task: TaskUI) => !deletedIdsRef.current.has(task.id));
          if (!setTasks(nextTasks, snapshot)) return;
          const nextTaskIds = new Set(nextTasks.map((task: TaskUI) => task.id));
          const nextDrafts = Object.fromEntries(
            Object.entries(draftsRef.current).filter(([taskId]) => {
              const task = nextTasks.find((item: TaskUI) => item.id === taskId);
              return nextTaskIds.has(taskId) && task && isUnscheduledActivity(task);
            })
          );
          draftsRef.current = nextDrafts;
          setDrafts(nextDrafts);
        }
      }
    } finally {
      setRefreshing(false);
    }
  }, [setTasks]);

  /** Persist the merged draft and readiness metadata unless locally deleted. Apply a successful response only when its version still matches, preserving newer edits; failures leave the draft available and mark save status as error. */
  const persistInlineTask = useCallback(
    async (task: TaskUI, options: { silent?: boolean; version?: number } = {}) => {
      if (deletedIdsRef.current.has(task.id)) return;
      const missing = getMissingPlanningFields(task, availableAttributesRef.current);
      setAutoSaveStatus(current => ({ ...current, [task.id]: 'saving' }));

      try {
        const customAttributes = {
          ...(task.customAttributes || {}),
          needs_review: missing.length > 0,
          required_fields_complete: missing.length === 0,
          missing_required_fields: missing,
        };
        const response = await api.tasks.update(task.id, {
          title: task.title,
          description: task.description,
          ...(!task.teamId && task.bundleId ? { bundle_id: task.bundleId } : {}),
          due_date: task.deadline,
          duration_minutes: task.estimatedDuration,
          priority: numberToPriority(task.priority || 3),
          state_profile: task.stateProfile,
          custom_attributes: customAttributes,
        });
        if (deletedIdsRef.current.has(task.id)) return;
        if (!response.success) throw new Error(response.error?.message || 'Save failed');
        if (
          options.version === undefined ||
          draftVersionsRef.current[task.id] === options.version
        ) {
          updateTask(task.id, taskToUI(response.data?.task || response.data));
          const nextDrafts = { ...draftsRef.current };
          delete nextDrafts[task.id];
          draftsRef.current = nextDrafts;
          setDrafts(nextDrafts);
          setAutoSaveStatus(current => ({ ...current, [task.id]: 'saved' }));
        } else {
          return;
        }
        if (!options.silent) {
          toast({
            title: missing.length ? 'Activity saved' : 'Activity ready',
            description: missing.length
              ? 'The remaining missing fields are still marked for review.'
              : 'Solve can now place this activity.',
          });
        }
      } catch (error) {
        setAutoSaveStatus(current => ({ ...current, [task.id]: 'error' }));
        if (!options.silent) {
          toast({
            title: 'Could not save row',
            description: error instanceof Error ? error.message : 'Try again.',
            variant: 'destructive',
          });
        }
        delete autoSaveTimersRef.current[task.id];
      }
    },
    [updateTask, toast]
  );

  /** Replace the pending timer for this activity and retain its captured draft version; edits to other rows keep independent timers. */
  const scheduleAutoSave = useCallback(
    (task: TaskUI, version: number, delayMs = 800) => {
      if (autoSaveTimersRef.current[task.id]) clearTimeout(autoSaveTimersRef.current[task.id]);
      autoSaveTimersRef.current[task.id] = setTimeout(() => {
        void persistInlineTask(task, { silent: true, version });
      }, delayMs);
    },
    [persistInlineTask]
  );

  /** Merge a row edit into the local draft, increment its version, and debounce the merged activity payload. The saved store record remains separate from the in-progress editor draft. */
  const updateInlineTask = useCallback(
    (taskId: string, updates: Partial<TaskUI>, options: { saveDelayMs?: number } = {}) => {
      const baseTask = useTasksStore.getState().tasks.find(task => task.id === taskId);
      if (!baseTask) return;

      const nextVersion = (draftVersionsRef.current[taskId] || 0) + 1;
      const nextDraft = mergeTaskDraft(draftsRef.current[taskId] || {}, updates);
      const nextDrafts = {
        ...draftsRef.current,
        [taskId]: nextDraft,
      };
      draftsRef.current = nextDrafts;
      draftVersionsRef.current[taskId] = nextVersion;
      setDrafts(nextDrafts);
      setAutoSaveStatus(current => ({ ...current, [taskId]: 'saving' }));

      scheduleAutoSave(mergeTaskDraft(baseTask, nextDraft), nextVersion, options.saveDelayMs);
    },
    [scheduleAutoSave]
  );

  /** Preserve blank as null, clamp score drafts, and derive the mode before passing the profile patch to the same versioned autosave path. */
  const updateInlineProfile = useCallback(
    (task: TaskUI, key: StateScoreKey, rawValue: string) => {
      const value = rawValue === '' ? null : Math.max(0, Math.min(10, Number(rawValue)));
      const stateProfile = {
        ...(task.stateProfile || {}),
        [key]: value,
      };
      updateInlineTask(task.id, {
        stateProfile: {
          ...stateProfile,
          mode: deriveTaskStateMode(stateProfile),
        },
      });
    },
    [updateInlineTask]
  );

  /** Convert numeric field drafts when nonempty and preserve other raw values, then enqueue the custom-attribute patch through row autosave. */
  const updateInlineAttribute = useCallback(
    (task: TaskUI, attr: AttributeDef, rawValue: string) => {
      const value =
        ['Number', 'float', 'integer'].includes(attr.data_type) && rawValue !== ''
          ? Number(rawValue)
          : rawValue;
      updateInlineTask(task.id, {
        customAttributes: {
          ...(task.customAttributes || {}),
          [attr.attr_id]: value,
        },
      });
    },
    [updateInlineTask]
  );

  /** Replace a saved attribute definition in both the current callback snapshot and visible row schema; unrelated definitions retain their identity. */
  const handleAttributeDefinitionUpdated = useCallback((updatedAttr: AttributeDef) => {
    availableAttributesRef.current = availableAttributesRef.current.map(attr =>
      attr.attr_id === updatedAttr.attr_id ? updatedAttr : attr
    );
    setAvailableAttributes(current =>
      current.map(attr => (attr.attr_id === updatedAttr.attr_id ? updatedAttr : attr))
    );
  }, []);

  /** Toggle one row without recreating the callback; cap selection at the server batch limit of 500. */
  const toggleSelected = useCallback((id: string) => {
    setSelectedIds(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else if (next.size < 500) next.add(id);
      return next;
    });
  }, []);

  /** Confirm deletion with current save/compute state, then invalidate old reads and reload retained children. */
  const handleDeleteTasks = useCallback(
    async (items: TaskUI[]) => {
      if (
        !items.length ||
        deleting ||
        inferring ||
        items.some(task => autoSaveStatus[task.id] === 'saving')
      )
        return;
      const label =
        items.length === 1 ? `"${items[0].title}"` : `${items.length} selected activities`;
      if (!window.confirm(`Delete ${label}? Unselected child activities will be kept.`)) return;
      setDeleting(true);
      ++refreshVersionRef.current;
      try {
        const response = await api.tasks.batchDelete(items.map(task => task.id));
        if (!response.success || !response.data)
          throw new Error(response.error?.message || 'Delete failed');
        const removed = new Set(response.data.deleted_ids);
        for (const id of removed) {
          deletedIdsRef.current.add(id);
          clearTimeout(autoSaveTimersRef.current[id]);
          delete autoSaveTimersRef.current[id];
          delete draftsRef.current[id];
          delete draftVersionsRef.current[id];
        }
        setDrafts({ ...draftsRef.current });
        deleteTasks([...removed]);
        // Invalidate pre-delete reads and reload retained children with server reparenting.
        void refresh();
        setSelectedIds(current => new Set([...current].filter(id => !removed.has(id))));
        toast({ title: `${removed.size} activit${removed.size === 1 ? 'y' : 'ies'} deleted` });
      } catch (error) {
        toast({
          title: 'Could not delete activities',
          description: error instanceof Error ? error.message : 'Try again.',
          variant: 'destructive',
        });
      } finally {
        setDeleting(false);
      }
    },
    [deleting, inferring, autoSaveStatus, deleteTasks, refresh, toast]
  );

  /** Latest committed deletion controller. A row click must see current save/compute guards without invalidating every row's memo boundary. */
  const deleteControllerRef = useRef(handleDeleteTasks);
  useLayoutEffect(() => {
    deleteControllerRef.current = handleDeleteTasks;
  }, [handleDeleteTasks]);
  /** Forward row actions to the latest committed controller; the surrounding fieldset owns shared busy-state disabling. */
  const deleteRowTasks = useCallback((items: TaskUI[]) => deleteControllerRef.current(items), []);

  // refresh reads the latest drafts and store each time; the effects below only decide when.
  const refreshActivities = useEffectEvent(() => {
    void refresh();
  });

  // Refresh after the screen has painted, so a mount or a keep-alive reveal shows at once.
  useEffect(
    () =>
      afterNextPaint(() => {
        refreshActivities();
        void loadWorkspace();
        void loadAttributes();
      }),
    [loadWorkspace]
  );

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refreshActivities();
    };
    const refreshOnFocus = () => refreshActivities();
    document.addEventListener('visibilitychange', refreshWhenVisible);
    window.addEventListener('focus', refreshOnFocus);
    return () => {
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      window.removeEventListener('focus', refreshOnFocus);
    };
  }, []);

  // On unmount, cancel pending autosaves and save every open draft. The effect event reads the
  // drafts and versions as they are at unmount, not as they were when the page mounted.
  const flushDraftsOnUnmount = useEffectEvent(() => {
    Object.values(autoSaveTimersRef.current).forEach(clearTimeout);
    Object.entries(draftsRef.current).forEach(([taskId, draft]) => {
      const baseTask = useTasksStore.getState().tasks.find(task => task.id === taskId);
      if (!baseTask) return;
      void persistInlineTask(mergeTaskDraft(baseTask, draft), {
        silent: true,
        version: draftVersionsRef.current[taskId],
      });
    });
  });
  useEffect(() => {
    return () => flushDraftsOnUnmount();
  }, []);

  const unscheduled = useMemo(() => {
    const candidates = tasks
      .filter(task => !deletedIdsRef.current.has(task.id) && isVisibleUnscheduledActivity(task))
      .map(task => mergeTaskDraft(task, drafts[task.id]));
    return dedupeImportedActivities(candidates)
      .filter(task => isVisibleUnscheduledActivity(task))
      .sort((a, b) => {
        const aDue = a.deadline ? new Date(a.deadline).getTime() : Infinity;
        const bDue = b.deadline ? new Date(b.deadline).getTime() : Infinity;
        return aDue - bDue || a.title.localeCompare(b.title);
      });
  }, [tasks, drafts]);

  const visibleUnscheduled = useMemo(
    () => unscheduled.slice(0, visibleLimit),
    [unscheduled, visibleLimit]
  );
  const needsDetails = useMemo(
    () =>
      unscheduled.filter(task => getMissingPlanningFields(task, availableAttributes).length > 0),
    [unscheduled, availableAttributes]
  );
  const activeBundles = useMemo(
    () => bundles.filter(bundle => bundle.status === 'active'),
    [bundles]
  );

  async function loadAttributes() {
    const response = await api.user.getAttributes();
    if (response.success && response.data) {
      availableAttributesRef.current = response.data;
      setAvailableAttributes(response.data);
    }
  }

  async function handleSaveTask(updates: Partial<TaskUI>, editingTask: TaskUI | null) {
    if (!editingTask) return;
    const response = await api.tasks.update(editingTask.id, {
      title: updates.title,
      description: updates.description,
      ...(!editingTask.teamId && updates.bundleId ? { bundle_id: updates.bundleId } : {}),
      due_date: updates.deadline,
      do_date: updates.scheduledStart,
      do_date_end: updates.scheduledEnd,
      duration_minutes: updates.estimatedDuration,
      priority: updates.priority ? numberToPriority(updates.priority) : undefined,
      state_profile: updates.stateProfile,
      split_policy: updates.splitPolicy,
      custom_attributes: updates.customAttributes,
    });

    if (response.success && response.data) {
      updateTask(editingTask.id, taskToUI(response.data?.task || response.data));
      toast({
        title: 'Activity updated',
        description: 'This activity can be scheduled once all required fields are complete.',
      });
    } else {
      toast({
        title: 'Could not save activity',
        description: response.error?.message || 'Try again.',
        variant: 'destructive',
      });
    }
  }

  /** Do not infer while any inline drafts are pending. Deduplicate selected IDs, run personal inference, and merge returned activities; unsupported evidence remains an explicit no-change result. */
  async function inferPlanningForTasks(taskIds: string[], options: { silent?: boolean } = {}) {
    if (Object.keys(draftsRef.current).length > 0) return;
    const ids = Array.from(new Set(taskIds)).filter(Boolean);
    if (ids.length === 0) {
      if (!options.silent) {
        toast({
          title: 'Nothing to infer',
          description: 'Visible activities already have inferred planning fields.',
        });
      }
      return;
    }

    setInferring(true);
    setInferenceStatus(`Inferring ${ids.length} activit${ids.length === 1 ? 'y' : 'ies'}...`);
    try {
      const selected = new Set(ids);
      const response = await inferActivityPlanning(
        tasks.filter(task => selected.has(task.id)),
        setInferenceStatus
      );
      const inferredTasks = Array.isArray(response.data?.tasks)
        ? response.data.tasks.map(taskToUI)
        : [];
      // Publish one canonical reply batch, retaining local-only UI fields and excluding rows
      // deleted while inference ran. Repeated store notifications otherwise rerender global UI.
      const inferredById = new Map<string, TaskUI>(inferredTasks.map(task => [task.id, task]));
      const committed = useTasksStore
        .getState()
        .tasks.filter(task => inferredById.has(task.id))
        .map(task => ({ ...task, ...inferredById.get(task.id)! }));
      useTasksStore.getState().upsertTasks(committed);
      const applied = Number(response.data?.applied || 0);
      setInferenceStatus(
        applied > 0
          ? `Inferred planning fields for ${applied}.`
          : 'Not enough personal evidence to fill the remaining fields. They were left unchanged.'
      );
      if (!options.silent) {
        toast({
          title: applied > 0 ? 'Planning inferred' : 'Inference complete',
          description:
            applied > 0
              ? 'Blank duration and P/RJ fields were filled without overwriting your entries.'
              : 'No supported predictions were available. Enter the remaining details yourself.',
        });
      }
    } catch (error) {
      setInferenceStatus('Inference failed.');
      if (!options.silent) {
        toast({
          title: 'Could not infer planning fields',
          description: error instanceof Error ? error.message : 'Try again.',
          variant: 'destructive',
        });
      }
    } finally {
      setInferring(false);
    }
  }

  const selectedTasks = unscheduled.filter(task => selectedIds.has(task.id));
  const savingSelected = selectedTasks.some(task => autoSaveStatus[task.id] === 'saving');

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-5">
      {purposeGate.dialog}
      <header className="flex flex-col gap-3 border-b pb-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-muted-foreground flex items-center gap-2 text-sm">
            <CalendarClock className="h-4 w-4" />
            <span>Unscheduled</span>
          </div>
          <h1 className="mt-2 text-3xl font-semibold tracking-normal">Unscheduled activities</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl text-sm">
            Review imported assignments and open activities before Solve is allowed to place them.
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <Button
            variant="outline"
            onClick={() =>
              inferPlanningForTasks(
                visibleUnscheduled
                  .filter(shouldInferActivityPlanning)
                  .slice(0, 30)
                  .map(task => task.id)
              )
            }
            disabled={
              deleting ||
              inferring ||
              Object.keys(drafts).length > 0 ||
              visibleUnscheduled.filter(shouldInferActivityPlanning).length === 0
            }
            className="w-full sm:w-auto"
          >
            <WandSparkles className={cn('mr-2 h-4 w-4', inferring && 'animate-pulse')} />
            Infer visible
          </Button>
          <Button
            variant="outline"
            onClick={refresh}
            disabled={refreshing || deleting}
            className="w-full sm:w-auto"
          >
            <RefreshCw className={cn('mr-2 h-4 w-4', refreshing && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </header>

      {inferenceStatus && (
        <div className="rounded-md border border-cyan-200 bg-cyan-50 px-3 py-2 text-sm text-cyan-950">
          {inferenceStatus}
        </div>
      )}

      <section className="grid gap-3 sm:grid-cols-3">
        <Metric label="Open" value={String(unscheduled.length)} />
        <Metric label="Needs details" value={String(needsDetails.length)} tone="amber" />
        <Metric
          label="Ready"
          value={String(unscheduled.length - needsDetails.length)}
          tone="green"
        />
      </section>

      {unscheduled.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" aria-label="Activity selection">
          <Button
            variant="outline"
            disabled={deleting || inferring}
            onClick={() =>
              setSelectedIds(new Set(visibleUnscheduled.slice(0, 500).map(task => task.id)))
            }
          >
            Select visible
          </Button>
          <Button
            variant="outline"
            disabled={deleting || inferring}
            onClick={() => setSelectedIds(new Set(unscheduled.slice(0, 500).map(task => task.id)))}
          >
            Select {unscheduled.length > 500 ? 'first 500' : 'all'}
          </Button>
          <Button
            variant="ghost"
            disabled={deleting || !selectedIds.size}
            onClick={() => setSelectedIds(new Set())}
          >
            Clear selection
          </Button>
          <span className="text-muted-foreground text-sm" aria-live="polite">
            {selectedTasks.length} selected
          </span>
          <Button
            variant="destructive"
            disabled={deleting || inferring || !selectedTasks.length || savingSelected}
            onClick={() => handleDeleteTasks(selectedTasks)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {deleting ? 'Deleting…' : 'Delete selected'}
          </Button>
          {savingSelected && (
            <span className="text-muted-foreground text-sm">Waiting for edits to save…</span>
          )}
        </div>
      )}

      <fieldset
        disabled={deleting || inferring}
        className="bg-card min-w-0 overflow-hidden rounded-md border"
      >
        {unscheduled.length === 0 ? (
          <div className="flex min-h-52 flex-col items-center justify-center px-4 py-12 text-center">
            <CheckCircle2 className="h-9 w-9 text-emerald-500" />
            <h2 className="mt-3 text-lg font-medium">All caught up</h2>
            <p className="text-muted-foreground mt-1 max-w-md text-sm">
              New Canvas assignments and open activities will appear here before they enter the
              schedule.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1500px] text-sm">
              <thead className="bg-muted/40 text-muted-foreground text-left text-xs">
                <tr>
                  <th className="bg-muted/40 sticky left-0 z-10 w-[280px] p-2">Activity</th>
                  <th className="w-[190px] p-2">Bundle</th>
                  <th className="w-[175px] p-2">Due</th>
                  <th className="w-[90px] p-2">Minutes</th>
                  <th className="w-[120px] p-2">Mode</th>
                  <th className="w-[90px] p-2">Req P</th>
                  <th className="w-[90px] p-2">Req RJ</th>
                  <th className="w-[90px] p-2">P return</th>
                  <th className="w-[90px] p-2">RJ return</th>
                  <th className="w-[90px] p-2">Resistance</th>
                  {availableAttributes.map(attr => (
                    <th key={attr.attr_id} className="w-[150px] p-2">
                      {attr.name}
                    </th>
                  ))}
                  <th className="w-[180px] p-2">Status</th>
                  <th className="bg-muted/40 sticky right-0 z-10 w-[120px] p-2 text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleUnscheduled.map(task => (
                  <UnscheduledRow
                    key={task.id}
                    task={task}
                    selected={selectedIds.has(task.id)}
                    selectionFull={selectedIds.size >= 500}
                    bundles={bundles}
                    activeBundles={activeBundles}
                    requirePurpose={purposeGate.requirePurpose}
                    availableAttributes={availableAttributes}
                    rowSaveStatus={autoSaveStatus[task.id]}
                    toggleSelected={toggleSelected}
                    updateInlineTask={updateInlineTask}
                    updateInlineProfile={updateInlineProfile}
                    updateInlineAttribute={updateInlineAttribute}
                    handleAttributeDefinitionUpdated={handleAttributeDefinitionUpdated}
                    setEditingTask={setEditingTask}
                    handleDeleteTasks={deleteRowTasks}
                  />
                ))}
              </tbody>
            </table>
            {visibleUnscheduled.length < unscheduled.length && (
              <div className="bg-card flex justify-center border-t p-3">
                <Button
                  variant="outline"
                  onClick={() => setVisibleLimit(value => Math.min(value + 80, unscheduled.length))}
                >
                  Show {Math.min(80, unscheduled.length - visibleUnscheduled.length)} more
                  activities
                </Button>
              </div>
            )}
          </div>
        )}
      </fieldset>

      <Detached state={editor} initial={null}>
        {editingTask => (
          <TaskEditor
            task={editingTask}
            open={Boolean(editingTask)}
            onClose={() => setEditingTask(null)}
            onSave={updates => handleSaveTask(updates, editingTask)}
            onDelete={editingTask ? () => handleDeleteTasks([editingTask]) : undefined}
          />
        )}
      </Detached>
    </div>
  );
}

/** Presentation inputs for one backlog row. Stable callbacks let unrelated selection changes skip its controls. */
interface UnscheduledRowProps {
  /** Current activity, including its local draft. */
  task: TaskUI;
  /** Whether this row is selected. */
  selected: boolean;
  /** Whether selecting another row would exceed the batch limit. */
  selectionFull: boolean;
  /** Workspace bundles used to display the current association. */
  bundles: WorkspaceBundleUI[];
  /** Active bundles offered by the picker. */
  activeBundles: WorkspaceBundleUI[];
  /** Asks for a bundle's purpose first when it has none, then runs the change; stable across renders. */
  requirePurpose: (bundleId: string | null | undefined, onReady: () => void) => void;
  /** Attribute schema shared by the table. */
  availableAttributes: AttributeDef[];
  /** Latest autosave status for this row only. */
  rowSaveStatus?: 'saving' | 'saved' | 'error';
  /** Toggle selection within the batch limit. */
  toggleSelected: (id: string) => void;
  /** Merge and debounce a row draft. */
  updateInlineTask: (
    id: string,
    updates: Partial<TaskUI>,
    options?: {
      /** Debounce override in milliseconds; omitted uses the existing 800 ms default. */ saveDelayMs?: number;
    }
  ) => void;
  /** Update one state score. */
  updateInlineProfile: (task: TaskUI, key: StateScoreKey, value: string) => void;
  /** Update one custom attribute. */
  updateInlineAttribute: (task: TaskUI, attr: AttributeDef, value: string) => void;
  /** Apply a changed attribute definition. */
  handleAttributeDefinitionUpdated: (attr: AttributeDef) => void;
  /** Open the full activity editor. */
  setEditingTask: (task: TaskUI) => void;
  /** Confirm and delete activities through the parent controller. */
  handleDeleteTasks: (tasks: TaskUI[]) => Promise<void>;
}

/** Render one editable activity. Default shallow comparison includes every prop, so changed callbacks and schemas cannot become stale. */
const UnscheduledRow = memo(function UnscheduledRow({
  task,
  selected,
  selectionFull,
  bundles,
  activeBundles,
  requirePurpose,
  availableAttributes,
  rowSaveStatus,
  toggleSelected,
  updateInlineTask,
  updateInlineProfile,
  updateInlineAttribute,
  handleAttributeDefinitionUpdated,
  setEditingTask,
  handleDeleteTasks,
}: UnscheduledRowProps) {
  const missing = getMissingPlanningFields(task, availableAttributes);
  const missingLabel = formatMissingPlanningFields(missing);
  const bundle = bundles.find(item => item.id === task.bundleId);
  const bundleLabel = task.bundleName || bundle?.name || null;
  const source = getSourceLabel(task);
  return (
    <tr key={task.id} className="border-t align-top">
      <td className="bg-card sticky left-0 z-10 p-2">
        <label className="text-muted-foreground mb-1 flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => toggleSelected(task.id)}
            aria-label={`Select ${task.title}`}
            disabled={!selected && selectionFull}
          />
          Select activity
        </label>
        <Input
          value={task.title}
          onChange={event => updateInlineTask(task.id, { title: event.target.value })}
          className="h-8 min-w-0 font-medium"
        />
        <div className="mt-1 flex flex-wrap items-center gap-1">
          {source && <Badge variant="secondary">{source}</Badge>}
          {getPlanningInference(task) && (
            <Badge className="bg-cyan-100 text-cyan-800 hover:bg-cyan-100">Inferred</Badge>
          )}
          {task.customAttributes?.local_title_override && (
            <Badge className="bg-cyan-100 text-cyan-800 hover:bg-cyan-100">Local name</Badge>
          )}
        </div>
        {getSourceTitle(task) && task.customAttributes?.local_title_override && (
          <p className="text-muted-foreground mt-1 truncate text-xs">
            Source: {getSourceTitle(task)}
          </p>
        )}
      </td>
      <td className="p-2">
        {task.teamId ? (
          <div className="rounded-md border border-cyan-200 bg-cyan-50 px-2 py-1.5 text-xs text-cyan-950 dark:border-cyan-900/60 dark:bg-cyan-950/30 dark:text-cyan-100">
            <div className="font-medium">{bundleLabel || 'Team bundle'}</div>
            <div className="mt-0.5 truncate text-[11px] opacity-80">
              {task.teamName || 'Team activity'}
              {task.projectName ? ` · ${task.projectName}` : ''}
            </div>
          </div>
        ) : (
          <>
            <select
              aria-label={`Bundle for ${task.title}`}
              className="border-input bg-background focus-visible:ring-ring h-8 w-full rounded-md border px-3 text-sm focus-visible:outline-none focus-visible:ring-1 disabled:opacity-50"
              value={task.bundleId || ''}
              onChange={event => {
                const bundleId = event.target.value;
                requirePurpose(bundleId, () => {
                  const nextBundle = activeBundles.find(item => item.id === bundleId);
                  updateInlineTask(task.id, {
                    bundleId,
                    projectId: nextBundle?.projectId || null,
                  });
                });
              }}
            >
              <option value="" disabled>
                Bundle
              </option>
              {activeBundles.map(item => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            {bundleLabel && (
              <p className="text-muted-foreground mt-1 truncate text-xs">{bundleLabel}</p>
            )}
          </>
        )}
      </td>
      <td className="p-2">
        <Input
          type="datetime-local"
          value={dateTimeInputValue(task.deadline)}
          onChange={event =>
            updateInlineTask(task.id, {
              deadline: event.target.value ? new Date(event.target.value).toISOString() : null,
            })
          }
          className="h-8"
        />
      </td>
      <td className="p-2">
        <NumberCell
          value={task.estimatedDuration}
          min={0}
          step={5}
          onChange={value =>
            updateInlineTask(task.id, { estimatedDuration: value }, { saveDelayMs: 150 })
          }
        />
      </td>
      <td className="p-2">
        <Badge variant="secondary">{formatMode(deriveTaskStateMode(task.stateProfile))}</Badge>
      </td>
      <ScoreCell task={task} field="required_productivity_energy" onChange={updateInlineProfile} />
      <ScoreCell task={task} field="required_rejuvenation_energy" onChange={updateInlineProfile} />
      <ScoreCell task={task} field="productivity_return" onChange={updateInlineProfile} />
      <ScoreCell task={task} field="rejuvenation_return" onChange={updateInlineProfile} />
      <ScoreCell task={task} field="resistance" onChange={updateInlineProfile} />
      {availableAttributes.map(attr => (
        <td key={attr.attr_id} className="p-2">
          <AttributeCell
            task={task}
            attr={attr}
            onChange={value => updateInlineAttribute(task, attr, value)}
            onAttributeUpdated={handleAttributeDefinitionUpdated}
          />
        </td>
      ))}
      <td className="p-2">
        {missing.length > 0 ? (
          <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span className="line-clamp-3">Missing {missingLabel}</span>
          </div>
        ) : (
          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Ready</Badge>
        )}
        <div
          className={cn(
            'mt-1 text-[11px]',
            rowSaveStatus === 'error'
              ? 'text-red-600'
              : rowSaveStatus === 'saving'
                ? 'text-muted-foreground'
                : 'text-emerald-600'
          )}
        >
          {rowSaveStatus === 'saving'
            ? 'Autosaving...'
            : rowSaveStatus === 'error'
              ? 'Autosave failed'
              : rowSaveStatus === 'saved'
                ? 'Saved'
                : getInferenceSummary(task) || 'Autosaves'}
        </div>
      </td>
      <td className="bg-card sticky right-0 z-10 p-2">
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setEditingTask(task)}
            aria-label={`Edit ${task.title}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleDeleteTasks([task])}
            disabled={rowSaveStatus === 'saving'}
            aria-label={`Delete ${task.title}`}
            className="text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
});

/** Treat an activity as unscheduled only when neither endpoint is a usable timestamp. */
function isUnscheduledActivity(task: TaskUI): boolean {
  return !hasUsableDate(task.scheduledStart) && !hasUsableDate(task.scheduledEnd);
}

/** Select unscheduled, unfinished, uncancelled activities that are not marked as duplicates. */
function isVisibleUnscheduledActivity(task: TaskUI): boolean {
  return (
    isUnscheduledActivity(task) &&
    task.status !== 'completed' &&
    task.status !== 'done' &&
    task.status !== 'cancelled' &&
    !task.customAttributes?.duplicate_of
  );
}

/** Request inference eligibility when duration is missing/nonpositive or a core P/RJ score is absent. This check does not guarantee usable personal evidence or approve a model write. */
function shouldInferActivityPlanning(task: TaskUI): boolean {
  const profile = task.stateProfile || {};
  const missingNativeEnergy =
    profile.required_productivity_energy == null ||
    profile.required_rejuvenation_energy == null ||
    profile.productivity_return == null ||
    profile.rejuvenation_return == null;
  const missingDuration = task.estimatedDuration == null || task.estimatedDuration <= 0;
  return missingNativeEnergy || missingDuration;
}

/** Read planning-inference metadata only when it is a non-array object; unrelated or malformed shapes return null. */
function getPlanningInference(task: TaskUI): Record<string, unknown> | null {
  const inference = task.customAttributes?.planning_inference;
  return inference && typeof inference === 'object' && !Array.isArray(inference) ? inference : null;
}

/** Format a stored inference confidence as a percentage when finite, otherwise show the generic inferred label. This display does not recalculate confidence. */
function getInferenceSummary(task: TaskUI): string | null {
  const inference = getPlanningInference(task);
  if (!inference) return null;
  const confidence = Number(inference.confidence);
  const confidenceLabel = Number.isFinite(confidence) ? `${Math.round(confidence * 100)}%` : '';
  return confidenceLabel ? `Inferred ${confidenceLabel}` : 'Inferred';
}

/** Return whether a nonempty value parses to a finite timestamp before using it as a scheduled endpoint. */
function hasUsableDate(value?: string | null): boolean {
  if (!value) return false;
  const date = new Date(value);
  return Number.isFinite(date.getTime());
}

/** Choose the highest-scoring Canvas activity for each import identity while preserving non-imported work. Deduplication changes this displayed collection only; no records are deleted from the server. */
function dedupeImportedActivities(tasks: TaskUI[]): TaskUI[] {
  const bestByIdentity = new Map<string, TaskUI>();
  const output: TaskUI[] = [];
  for (const task of tasks) {
    const identity = importedActivityIdentity(task);
    if (!identity) {
      output.push(task);
      continue;
    }
    const current = bestByIdentity.get(identity);
    if (!current || scoreImportedActivity(task) > scoreImportedActivity(current)) {
      bestByIdentity.set(identity, task);
    }
  }
  const keptImportedIds = new Set([...bestByIdentity.values()].map(task => task.id));
  for (const task of tasks) {
    const identity = importedActivityIdentity(task);
    if (identity) {
      if (keptImportedIds.has(task.id)) output.push(task);
      continue;
    }
  }
  return output;
}

/** Prefer Canvas event ID, then calendar/event pair, then normalized source-title plus deadline as the display-deduplication identity. Other providers are not grouped by this helper. */
function importedActivityIdentity(task: TaskUI): string | null {
  if (String(task.customAttributes?.source || '').toLowerCase() !== 'canvas') return null;
  const canvas =
    task.customAttributes?.canvas && typeof task.customAttributes.canvas === 'object'
      ? task.customAttributes.canvas
      : {};
  if (typeof canvas.event_id === 'string' && canvas.event_id.trim())
    return `canvas:event:${canvas.event_id}`;
  if (
    typeof canvas.google_event_id === 'string' &&
    canvas.google_event_id.trim() &&
    typeof canvas.calendar_id === 'string' &&
    canvas.calendar_id.trim()
  ) {
    return `canvas:gcal:${canvas.calendar_id}:${canvas.google_event_id}`;
  }
  const sourceTitle =
    typeof canvas.source_title === 'string' && canvas.source_title.trim()
      ? canvas.source_title
      : typeof task.customAttributes?.external_source_title === 'string'
        ? task.customAttributes.external_source_title
        : task.title;
  const dueAt =
    typeof canvas.due_at === 'string' && canvas.due_at.trim() ? canvas.due_at : task.deadline || '';
  return `canvas:title:${String(sourceTitle).toLowerCase().replace(/\s+/g, ' ').trim()}:${dueAt}`;
}

/** Rank duplicate import candidates by reviewed completeness, valid placement, state fields, bundle/duration, completion, and recency. The heuristic chooses a displayed representative, not canonical identity. */
function scoreImportedActivity(task: TaskUI): number {
  const profile = task.stateProfile || {};
  const filledStateFields = [
    profile.required_productivity_energy,
    profile.required_rejuvenation_energy,
    profile.productivity_return,
    profile.rejuvenation_return,
    profile.resistance,
  ].filter(value => value !== null && value !== undefined && Number.isFinite(Number(value))).length;
  const updatedAt = Date.parse(task.updatedAt || task.createdAt || '');
  return (
    (task.customAttributes?.required_fields_complete === true ? 1000 : 0) +
    (task.customAttributes?.needs_review === false ? 250 : 0) +
    (hasUsableDate(task.scheduledStart) && hasUsableDate(task.scheduledEnd) ? 500 : 0) +
    filledStateFields * 25 +
    (task.bundleId ? 20 : 0) +
    (task.estimatedDuration ? 10 : 0) +
    Math.min(10, Number(task.completionPercent || 0)) +
    (Number.isFinite(updatedAt) ? Math.min(20, updatedAt / 1e12) : 0)
  );
}

/** Display an unscheduled-work summary value with an optional status tone; the parent computes the count or readiness statistic. */
function Metric({
  label,
  value,
  tone = 'default',
}: {
  /** Visible caption describing this control or metric. */
  label: string;
  /** Preformatted metric value displayed without additional measurement. */
  value: string;
  /** Presentation color for the metric category. Defaults to the neutral style. */
  tone?: 'default' | 'amber' | 'green';
}) {
  const toneClass =
    tone === 'amber'
      ? 'border-amber-200 bg-amber-50 text-amber-950'
      : tone === 'green'
        ? 'border-emerald-200 bg-emerald-50 text-emerald-950'
        : 'border-border bg-card text-foreground';
  return (
    <div className={cn('rounded-md border p-3', toneClass)}>
      <div className="text-muted-foreground text-xs uppercase">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}

/** Overlay an activity draft while merging stateProfile and customAttributes one level deep so independent edited fields survive. Without a draft, return the original object. */
function mergeTaskDraft<T extends Partial<TaskUI>>(base: T, draft?: Partial<TaskUI>): T {
  if (!draft) return base;
  return {
    ...base,
    ...draft,
    stateProfile:
      base.stateProfile || draft.stateProfile
        ? {
            ...(base.stateProfile || {}),
            ...(draft.stateProfile || {}),
          }
        : undefined,
    customAttributes:
      base.customAttributes || draft.customAttributes
        ? {
            ...(base.customAttributes || {}),
            ...(draft.customAttributes || {}),
          }
        : undefined,
  };
}

/** Edit an optional numeric planning value without turning an empty cell into zero. HTML bounds guide input; the owning draft/save path remains responsible for validation. */
function NumberCell({
  value,
  min = 0,
  max,
  step = 0.5,
  onChange,
}: {
  /** Numeric draft value; null or undefined displays an empty cell. */
  value?: number | null;
  /** Lower HTML numeric bound; defaults to zero. */
  min?: number;
  /** Optional upper HTML numeric bound; omitted means no bound is supplied. */
  max?: number;
  /** HTML numeric step; defaults to 0.5. */
  step?: number;
  /** Receive null for an empty cell or the Number-converted draft value. */
  onChange: (value: number | null) => void;
}) {
  return (
    <Input
      type="number"
      min={min}
      max={max}
      step={step}
      value={value ?? ''}
      onChange={event => onChange(event.target.value === '' ? null : Number(event.target.value))}
      className="h-8 min-w-[4.5rem] px-2 tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
    />
  );
}

/** Select one state-profile field from an activity and edit it through a 0–10 number cell. The parent receives the activity, field key, and string draft for its save queue. */
function ScoreCell({
  task,
  field,
  onChange,
}: {
  /** Activity record whose planning fields are displayed or edited. */
  task: TaskUI;
  /** State-profile numeric field to display and edit; the mode field is excluded. */
  field: StateScoreKey;
  /** Receive the activity, score-field key, and raw string; an empty string clears the draft score. */
  onChange: (task: TaskUI, key: StateScoreKey, rawValue: string) => void;
}) {
  const value = task.stateProfile?.[field];
  return (
    <td className="p-2">
      <NumberCell
        value={typeof value === 'number' ? value : null}
        min={0}
        max={10}
        step={0.5}
        onChange={next => onChange(task, field, next == null ? '' : String(next))}
      />
    </td>
  );
}

/** Numeric state-profile keys used by editable table score cells; excludes the derived mode. */
type StateScoreKey = Exclude<keyof TaskStateProfile, 'mode'>;

/** Resolve an activity’s custom attribute by its planning identity and render the appropriate editor. Enum changes can update the definition; ordinary edits report only a string value. */
function AttributeCell({
  task,
  attr,
  onChange,
  onAttributeUpdated,
}: {
  /** Activity record whose planning fields are displayed or edited. */
  task: TaskUI;
  /** Custom field definition controlling type, choices, and planning-value lookup. */
  attr: AttributeDef;
  /** Report the edited draft value to the owner; this callback does not itself prove a saved server result. */
  onChange: (value: string) => void;
  /** Replace the parent’s field definition after an enum option is added or changed. */
  onAttributeUpdated: (attr: AttributeDef) => void;
}) {
  const value = getPlanningAttributeValue(task.customAttributes, attr) ?? '';
  if (attr.data_type === 'enum') {
    return (
      <EnumAttributeInput
        attr={attr}
        value={value}
        onChange={onChange}
        onAttributeUpdated={onAttributeUpdated}
        compact
      />
    );
  }

  return (
    <Input
      type={['Number', 'float', 'integer'].includes(attr.data_type) ? 'number' : 'text'}
      value={String(value)}
      onChange={event => onChange(event.target.value)}
      className="h-8"
    />
  );
}

/** Preserve the legacy local end-of-day form and otherwise format an instant for the browser-local datetime input. Missing or invalid dates display blank. */
function dateTimeInputValue(value?: string | null): string {
  if (!value) return '';
  const localEndOfDay = value.match(/^(\d{4})-(\d{2})-(\d{2})T23:59:00$/);
  if (localEndOfDay) return `${localEndOfDay[1]}-${localEndOfDay[2]}-${localEndOfDay[3]}T23:59`;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

/** Map recognized Canvas, Google Calendar, and Book-Tracker source metadata to a display label; unknown sources remain unlabeled. */
function getSourceLabel(task: TaskUI): string | null {
  const source = String(task.customAttributes?.source || '').toLowerCase();
  if (source === 'canvas') return 'Canvas';
  if (source === 'google-calendar' || source === 'gcal') return 'Google Calendar';
  if (source === 'book-tracker') return 'Book-Tracker';
  return null;
}

/** Prefer the nested Canvas source title, then the general external-source title, without replacing the editable activity title. */
function getSourceTitle(task: TaskUI): string | null {
  const canvas = task.customAttributes?.canvas;
  if (canvas && typeof canvas === 'object' && typeof canvas.source_title === 'string') {
    return canvas.source_title;
  }
  return typeof task.customAttributes?.external_source_title === 'string'
    ? task.customAttributes.external_source_title
    : null;
}

/** Map the derived state mode to a display label, falling back to Neutral for the remaining case. */
function formatMode(mode: NonNullable<TaskStateProfile['mode']>): string {
  switch (mode) {
    case 'productive':
      return 'Productive';
    case 'rejuvenating':
      return 'Rejuvenating';
    case 'both':
      return 'Both';
    default:
      return 'Neutral';
  }
}
